import React, { useState, useEffect, useRef } from 'react';
import { FaCheck, FaCloudUploadAlt, FaDatabase, FaServer, FaArrowRight, FaAngleRight, FaBookOpen, FaGraduationCap } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

// Import course images
import c1Image from '../../assets/images/c1.png';
import c2Image from '../../assets/images/c2.png';
import c3Image from '../../assets/images/c3.png';

const CoursesSection = ({ openContactModal }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hovered, setHovered] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hoverPosition, setHoverPosition] = useState('top');
  const courseDetailsRef = useRef(null);

  // Set hover position based on index
  const handleMouseEnter = (idx, totalItems) => {
    setHovered(idx);
    // If it's one of the last two items, position at bottom
    if (idx >= totalItems - 2) {
      setHoverPosition('bottom');
    } else {
      setHoverPosition('top');
    }
  };

  // Handle course card click
  const handleCourseClick = (index) => {
    setActiveIndex(index);
    
    // Scroll to course details section after a short delay
    setTimeout(() => {
      if (courseDetailsRef.current) {
        // Calculate header height plus some extra space
        const headerHeight = 80; // Approximate header height
        const extraSpace = 20; // Extra space for visual comfort
        
        // Get the element's position relative to the viewport
        const rect = courseDetailsRef.current.getBoundingClientRect();
        
        // Calculate where to scroll
        const scrollTop = window.pageYOffset + rect.top - headerHeight - extraSpace;
        
        // Scroll to the calculated position
        window.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('courses');
    if (section) observer.observe(section);

    return () => {
      if (section) observer.unobserve(section);
    };
  }, []);

  const courses = [
    {
      id: 1,
      title: "DevOps Engineering",
      icon: <FaServer className="course-icon" />,
      image: c1Image,
      description: "Master the DevOps methodology and tools to automate software delivery processes and improve collaboration between development and operations teams.",
      duration: "12 weeks",
      level: "Beginner to Advanced",
      price: "₹24,999",
      highlights: [
        "CI/CD Pipeline Implementation with Jenkins and GitHub Actions",
        "Infrastructure as Code with Terraform",
        "Container Orchestration with Docker and Kubernetes",
        "Microservices Architecture and Implementation",
        "Configuration Management with Ansible",
        "Monitoring and Logging Solutions"
      ],
      modules: [
        {
          title: "DevOps Fundamentals",
          topics: ["DevOps Principles and Culture", "Git Version Control", "Linux Administration Essentials", "Bash Scripting"]
        },
        {
          title: "Continuous Integration & Delivery",
          topics: ["Jenkins Pipeline Setup", "GitHub Actions Workflows", "Automated Testing Strategies", "Deployment Automation"]
        },
        {
          title: "Containerization",
          topics: ["Docker Fundamentals", "Container Networking", "Docker Compose", "Container Security"]
        },
        {
          title: "Kubernetes Orchestration",
          topics: ["Kubernetes Architecture", "Pod Management", "Deployments and Services", "Helm Charts"]
        },
        {
          title: "Infrastructure as Code",
          topics: ["Terraform Fundamentals", "Resource Provisioning", "State Management", "Modules and Best Practices"]
        }
      ],
      color: "#1D4ED8",
      gradient: "linear-gradient(135deg, #1D4ED8, #104c7b)"
    },
    {
      id: 2,
      title: "Cloud Architecture",
      icon: <FaCloudUploadAlt className="course-icon" />,
      image: c2Image,
      description: "Design, build, and manage scalable, highly available, and fault-tolerant systems on cloud platforms with focus on AWS services and best practices.",
      duration: "12 weeks",
      level: "Beginner to Advanced",
      price: "₹14,999",
      highlights: [
        "AWS Core Services Implementation",
        "Multi-region High Availability Architectures",
        "Cloud Security and Compliance",
        "Cost Optimization Strategies",
        "Serverless Architecture Design",
        "Disaster Recovery Planning"
      ],
      modules: [
        {
          title: "AWS Fundamentals",
          topics: ["AWS Global Infrastructure", "IAM and Security", "VPC Design", "EC2 and ELB"]
        },
        {
          title: "Storage and Database Services",
          topics: ["S3 and Glacier", "RDS and DynamoDB", "ElastiCache", "Storage Gateway"]
        },
        {
          title: "Application Services",
          topics: ["SQS and SNS", "API Gateway", "Step Functions", "AppSync"]
        },
        {
          title: "Serverless Computing",
          topics: ["Lambda Functions", "Event-driven Architecture", "SAM Framework", "Serverless Patterns"]
        },
        {
          title: "High Availability Design",
          topics: ["Multi-AZ and Multi-Region", "Auto Scaling", "Route 53 Strategies", "Disaster Recovery Options"]
        }
      ],
      color: "#0077b6",
      gradient: "linear-gradient(135deg, #0077b6, #104c7b)"
    },
    {
      id: 3,
      title: "Data Engineering",
      icon: <FaDatabase className="course-icon" />,
      image: c3Image,
      description: "Learn to design and build data pipelines, implement data processing systems, and manage large-scale data infrastructure using cloud services.",
      duration: "12 weeks",
      level: "Beginner to Advanced",
      price: "₹24,999",
      highlights: [
        "ETL Pipeline Development",
        "Big Data Processing with Apache Spark",
        "Data Lakes and Warehouses on AWS",
        "Real-time Streaming with Kafka",
        "Data Governance and Security",
        "Machine Learning Operations (MLOps)"
      ],
      modules: [
        {
          title: "Data Engineering Fundamentals",
          topics: ["Data Modeling", "SQL and NoSQL Databases", "Python for Data Engineering", "Data Quality"]
        },
        {
          title: "Batch Processing",
          topics: ["ETL/ELT Patterns", "Apache Spark", "AWS Glue", "Airflow Orchestration"]
        },
        {
          title: "Stream Processing",
          topics: ["Kafka Architecture", "AWS Kinesis", "Stream Processing Patterns", "Real-time Analytics"]
        },
        {
          title: "Data Storage",
          topics: ["Data Lake Design on S3", "Redshift Data Warehouse", "Athena and Lake Formation", "Storage Optimization"]
        },
        {
          title: "MLOps and Advanced Topics",
          topics: ["ML Pipelines", "Feature Stores", "Model Deployment", "Monitoring ML Systems"]
        }
      ],
      color: "#F97316",
      gradient: "linear-gradient(135deg, #F97316, #E67E22)"
    }
  ];

  // Get active course
  const activeCourse = courses[activeIndex];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <section id="courses" className="skillraiz-courses-section">
      <div className="container">
        <motion.div 
          className="courses-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">Specialized Learning Paths</h2>
          <div className="title-underline"></div>
          <p className="section-description">
            Elevate your career with our industry-aligned courses designed to transform you into a technology professional.
          </p>
        </motion.div>

        <motion.div 
          className="skillraiz-courses-grid"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {courses.map((course, index) => (
            <motion.div 
              key={course.id}
              className={`skillraiz-course-card ${index === activeIndex ? 'active' : ''}`}
              variants={itemVariants}
              onClick={() => handleCourseClick(index)}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              style={index === activeIndex ? { borderLeftColor: course.color } : {}}
            >
              <div className="card-decoration" style={{ background: course.gradient }}></div>
              <div className="skillraiz-course-icon" style={{ color: course.color }}>
                {course.icon}
              </div>
              <div className="skillraiz-course-info">
                <h3 className="skillraiz-course-title" style={{ color: course.color }}>{course.title}</h3>
                <p className="skillraiz-course-brief">{course.description.split('.')[0]}.</p>
                <div className="skillraiz-course-details">
                  <span className="skillraiz-course-duration">
                    <FaGraduationCap size={14} />
                    {course.duration}
                  </span>
                  <span className="skillraiz-course-level">{course.level}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div 
            key={activeIndex}
            ref={courseDetailsRef}
            className="skillraiz-course-details-minimal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            style={{ borderTopColor: activeCourse.color }}
          >
            <div className="minimal-course-header">
              <div className="minimal-course-info">
                <motion.h2 
                  className="minimal-course-title"
                  style={{ color: activeCourse.color }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {activeCourse.title}
                </motion.h2>
                <motion.div 
                  className="minimal-course-meta"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <span className="minimal-course-duration">
                    <FaGraduationCap />
                    {activeCourse.duration}
                  </span>
                  <span className="minimal-divider"></span>
                  <span className="minimal-course-level">{activeCourse.level}</span>
                </motion.div>
              </div>
              <div className="minimal-header-right">
                <div className="minimal-course-price" style={{ color: activeCourse.color }}>
                  {activeCourse.price}
                </div>
                <motion.button 
                  className="minimal-enroll-button"
                  onClick={openContactModal}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{ background: activeCourse.gradient }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                >
                  Enroll Now
                </motion.button>
              </div>
            </div>
            
            <div className="minimal-course-image-container">
              <img 
                src={activeCourse.image} 
                alt={activeCourse.title} 
                className="minimal-course-image"
              />
              <div className="minimal-image-overlay" style={{ 
                background: `linear-gradient(to right, ${activeCourse.color}80, transparent)` 
              }}></div>
              <div className="minimal-image-text">
                <h3>Master {activeCourse.title}</h3>
                <p>Industry-relevant skills for your career growth</p>
              </div>
            </div>

            <div className="minimal-course-content">
              <div className="minimal-course-description">
                <p>{activeCourse.description}</p>
              </div>

              <div className="minimal-columns">
                <motion.div 
                  className="minimal-modules"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                >
                  <h3 className="minimal-section-title">
                    <div className="minimal-title-dot" style={{ backgroundColor: activeCourse.color }}></div>
                    Course Curriculum
                  </h3>
                  <div className="minimal-modules-list">
                    {activeCourse.modules.map((module, idx) => (
                      <div 
                        key={idx} 
                        className="minimal-module-item-hover"
                        onMouseEnter={() => handleMouseEnter(idx, activeCourse.modules.length)}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <div className="minimal-module-title-hover">
                          <div className="minimal-module-number" style={{ color: activeCourse.color, borderColor: activeCourse.color }}>
                            {idx + 1}
                          </div>
                          <span>{module.title}</span>
                        </div>
                        {hovered === idx && (
                          <motion.div 
                            className={`minimal-module-topics-hover position-${hoverPosition}`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            style={{ 
                              background: `rgba(255, 255, 255, 0.85)`,
                              borderLeft: `3px solid ${activeCourse.color}`
                            }}
                          >
                            <div className="glass-container-header">
                              <span style={{ color: activeCourse.color }}>{module.title}</span>
                            </div>
                            {module.topics.map((topic, topicIdx) => (
                              <div key={topicIdx} className="minimal-topic">
                                <div className="minimal-topic-bullet" style={{ backgroundColor: activeCourse.color }}></div>
                                <span>{topic}</span>
                              </div>
                            ))}
                          </motion.div>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>

                <motion.div 
                  className="minimal-highlights"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  <h3 className="minimal-section-title">
                    <div className="minimal-title-dot" style={{ backgroundColor: activeCourse.color }}></div>
                    Key Technologies
                  </h3>
                  <div className="minimal-highlights-list">
                    {activeCourse.highlights.map((highlight, idx) => (
                      <motion.div
                        key={idx}
                        className="minimal-highlight-item"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * idx, duration: 0.3 }}
                      >
                        <div className="minimal-check" style={{ color: activeCourse.color }}>
                          <FaCheck />
                        </div>
                        <span>{highlight}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default CoursesSection; 